export interface FieldNote {
  slug: string;
  title: string;
  date: string; // ISO format: "2026-03-11"
  excerpt: string;
  content: string;
  tags: string[];
}

export const fieldNotes: FieldNote[] = [
  {
    slug: "on-camera",
    title: "On Camera",
    date: "2026-09-13",
    excerpt: "For a year I built Maslow where nobody could see it. That ended with a urinal.",
    content: `For a year I built Maslow where nobody could see it. A bench in a friend's basement, a codebase, a stack of drawings, a business plan on its fourth rewrite. I told myself that was focus. It was also hiding.

This week I posted a video of a urinal.

The series is called Prison or Public? Two kinds of fixture: the stainless one you have seen in a holding cell and the porcelain one you have seen in a park, and the question of which one a city thinks you deserve. A few hundred people watched it. Strangers left comments. Nobody was cruel. One person followed the account before I ever mentioned it to him, which is the first time anyone has found Maslow without me standing in front of them explaining it.

So here is the decision. Maslow gets built in public from now on: the clunk, the light, the terrazzo, the paperwork, the parts that do not work yet. I will be on camera talking about it, which I do not enjoy and am going to do anyway. Being seen was the bottleneck, and the bottleneck was me.

The account is @maslow.nyc, if you want to watch it happen.`,
    tags: ["field", "building in public"],
  },
  {
    slug: "the-clunk",
    title: "The Clunk",
    date: "2026-07-31",
    excerpt: "Not a click — a clunk. The sound of a door deciding to be a wall for a while.",
    content: `There's a sound a good lock makes. Not a click — a clunk. Weight moving, metal seating, a door deciding to be a wall for a while.

We've been building that sound on a workbench: an electric strike, the part of the frame that holds the latch and lets go on command. It's the piece that decides how a door behaves when things go wrong — power cut, session over, someone needs out.

Here's the rule we build to: the room protects you while you're in it, and it never traps you. You can always leave — the handle works from inside, every time, no power required. When your session ends, the door unlocks itself. No one gets locked in. That's not a feature, it's a promise, and it's enforced by hardware, not software.

So we sit at a bench and fire a strike over and over, listening. Too sharp and it sounds cheap. Too quiet and you don't trust it. Somewhere in the middle is the sound of being safe in a city that doesn't offer that sound often enough.

Clunk.`,
    tags: ["build", "safety"],
  },
  {
    slug: "terrazzo-from-broken-toilets",
    title: "Terrazzo From Broken Toilets",
    date: "2026-07-31",
    excerpt: "Porcelain is a beautiful material. It just had a bad job.",
    content: `New York throws away a lot of toilets. Cracked tanks, chipped bowls, renovation casualties — porcelain by the dumpsterful, headed for landfill.

Porcelain is a beautiful material. It just had a bad job.

The counters in our rooms will be terrazzo cast with crushed porcelain from broken toilets. Ground down, set in the surface, polished flat. The city's old problem becomes the surface of its fix — and you can run your hand across it.

Under each counter sits a refurbished vintage sideboard, a different find in every room, raised open underneath so a wheelchair rolls right up. Nothing matches. Everything belongs.

We're at the smashing-and-testing stage: breaking porcelain, sorting the pieces, seeing what the chips look like set and polished. Some of it is lovely. Some of it looks like a crime scene in a china shop. That's what prototyping is for.`,
    tags: ["build", "materials"],
  },
  {
    slug: "the-light-youll-never-notice",
    title: "The Light You'll Never Notice",
    date: "2026-07-31",
    excerpt: "Most restroom lighting is an insult delivered from the ceiling. Our rooms don't have windows — so we're building the window.",
    content: `Most restroom lighting is an insult delivered from the ceiling. A cold tube, straight down, the kind of light that hollows your face and makes 2 p.m. feel like a holding cell.

Our rooms don't have windows. So we're building the window.

The skylight in every Maslow room is artificial — a panel of warm, even light that follows the day. Morning light in the morning, softer and warmer as the evening comes on. It's tuned to a circadian curve, and it's flicker-free at camera shutter speeds, because we know exactly what you're going to do in front of that mirror, and the light should love you for it.

The spec that matters most is one almost nobody publishes: how a light renders red. Skin tone lives in the reds. Most cheap LEDs score terribly there, which is why you look gray in gas-station bathrooms. We reject panels on that number.

Right now the skylight is a bench prototype — strips, diffusers, a dimmer, and an embarrassing number of measurements. We learned the hard way that a diffuser needs real distance from the LEDs to erase them into one sheet of light. Too close and you see dots. There's math for it. We do the math now.

The goal is a light you never notice. You just look like yourself, on a good day.`,
    tags: ["build", "light"],
  },
  {
    slug: "why-we-built-maslow",
    title: "Why We Built Maslow",
    date: "2026-03-11",
    excerpt: "Eight and a half million people. Eleven hundred public restrooms. The math has never made sense.",
    content: `Eight and a half million people live in New York City. There are fewer than 1,100 public restrooms.

That gap is not inconvenience. It is daily indignity — imposed on workers, tourists, parents, people with medical needs, and anyone who doesn't have $7 to justify using a Starbucks.

Maslow exists because we believe the floor should be higher. Not as a charity. As a business. Premium is not an upgrade — it's the starting point.

We're building in SoHo first. Twelve rooms. Automated sanitization. Walk-up access. No membership required.

This is Field Notes — where we think out loud about what we're building, why restrooms matter, and what the city could be.`,
    tags: ["origin", "mission"],
  },
];

export function getFieldNoteBySlug(slug: string): FieldNote | undefined {
  return fieldNotes.find(note => note.slug === slug);
}

export function formatDate(dateString: string): string {
  // Parse as local noon, not UTC midnight: "2026-07-31" was rendering as July 30 in New York.
  const date = new Date(`${dateString}T12:00:00`);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
